import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { createCompany, listIndustries, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";

export default function CreateCompanyDialog({ open, onOpenChange, onCreated }) {
  const [industries, setIndustries] = useState([]);
  const [form, setForm] = useState({
    name: "",
    industry: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      listIndustries().then(setIndustries).catch(() => {});
    }
  }, [open]);

  const resetForm = () =>
    setForm({ name: "", industry: "", admin_name: "", admin_email: "", admin_password: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.industry || !form.admin_name.trim() || !form.admin_email.trim() || !form.admin_password) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await createCompany(form);
      toast.success("Company created");
      resetForm();
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add company</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="create-company-form">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
              Company name *
            </Label>
            <Input
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Acme Corp"
              data-testid="company-name-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
              Industry *
            </Label>
            <SearchableSelect
              value={form.industry}
              onValueChange={(v) => set("industry", v)}
              options={industries.map((ind) => ({ value: ind.id, label: ind.label }))}
              placeholder="Select industry"
              testId="company-industry-select"
            />
          </div>

          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs text-slate-500 mb-3 font-medium">First admin account</p>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  Admin name *
                </Label>
                <Input
                  required
                  value={form.admin_name}
                  onChange={(e) => set("admin_name", e.target.value)}
                  placeholder="Jane Smith"
                  data-testid="company-admin-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  Admin email *
                </Label>
                <Input
                  required
                  type="email"
                  value={form.admin_email}
                  onChange={(e) => set("admin_email", e.target.value)}
                  placeholder="jane@example.com"
                  data-testid="company-admin-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  Admin password *
                </Label>
                <PasswordInput
                  required
                  value={form.admin_password}
                  onChange={(e) => set("admin_password", e.target.value)}
                  placeholder="Min 8 characters"
                  data-testid="company-admin-password-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { resetForm(); onOpenChange(false); }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={submitting}
              data-testid="create-company-submit"
            >
              {submitting ? "Creating…" : "Create company"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
